import { useCallback, useState } from 'react';
import { message } from 'antd';

export interface LevelFieldProp {
  cnName: string;
  enName?: string;
  constraint?: {
    maxLength: number;
    elementType: string;
  };
}
export interface LevelConfigProp {
  maxLevel: number;
  minLevel: number;
  fields: LevelFieldProp[];
}

export interface TitleProp {
  cnName?: string;
  enName?: string;
  key?: string;

  textConfig?: {
    maxLength: boolean;
    cnPlaceHolder: string;
    enPlaceHolder: string;
  };
  pictureConfig?: {
    isVisible?: boolean;
    maxSize?: number;
    suffixes?: string[];
  };
}

interface ConfigImageProp {
  elementType: string;
  maxSize: number;
  suffixes?: string[];
}

export interface BGConfigProp {
  background?: ConfigImageProp;
  collapse: boolean;
}

interface TitleInputProp {
  maxLength: number;
  cnPlaceHolder: string;
  enPlaceHolder: string;
  elementType: string;
}

export interface TitleConfigProp {
  collapse: boolean;
  navTitle?: TitleInputProp;
  navTitleImage?: ConfigImageProp;
  subTitle?: TitleInputProp;
  subTitleImage?: ConfigImageProp;
  title?: TitleInputProp;
  titleImage?: ConfigImageProp;
}

interface DifficultyGroupConfigProp {
  dropDownOptions: [
    {
      value?: string;
      name?: string;
    }
  ];
  elementType: string;
}

interface ContentConfigProp {
  backgroundGroupConfig?: BGConfigProp;
  titleGroupConfig?: TitleConfigProp;
  instructionGroupConfig?: { collapse: boolean };
  scoreGroupConfig?: { collapse: boolean };
  timeGroupConfig?: { collapse: boolean };
  goldGroupConfig?: { collapse: boolean };
  customizedTagGroupConfig?: { collapse: boolean };
  copyrightGroupConfig?: { collapse: boolean };
  difficultyGroupConfig?: DifficultyGroupConfigProp;
}

interface CoverConfigProp {
  suffixes: string[];
  maxSize: number;
}

interface InstructionConfigProp {
  cnPlaceHolder: string;
  enPlaceHolder: string;
  maxLength: number;
}

interface NameConfigProp {
  cnPlaceHolder: string;
  enPlaceHolder: string;
  maxLength: number;
}

interface IndependentBizConfigProp {
  rechargeable: boolean;
  supportClassification: boolean;
  supportCustomizedTag: boolean;
  coverConfig: CoverConfigProp;
  instructionConfig: InstructionConfigProp;
  nameConfig: NameConfigProp;
}

export interface StyleConfigItemProp {
  nodeUrl?: string;
  platform?: string;
  contentUrl?: string;
  courseUrl?: string;
}

export interface StyleConfigProp {
  ENGLISH?: StyleConfigItemProp;
  COMMON?: StyleConfigItemProp;
  TOTS?: StyleConfigItemProp;
}

export interface BizConfigProp {
  cnName: string;
  enName: string;
  hasContent?: boolean;
  maxLevel?: number;
  minLevel?: number;
  courseBusinessConfig?: {
    mountCatalogueType?: string;
    isCourseware?: boolean;
    isTask?: boolean;
  };
  parentUuid?: string;
  repeatableAtSameStructure?: boolean;
  subjectList?: string[];
  uuid?: string;
  levelConfig?: LevelConfigProp;
  contentSettingConfig?: ContentConfigProp;
  intoContentRepository?: boolean;
  independentBusinessConfig?: IndependentBizConfigProp;
  templateRepeatable?: boolean;
  styleConfig?: StyleConfigProp;
}
function useBizConfig() {
  const [bizConfig, setBizConfig] = useState<BizConfigProp>({
    cnName: null,
    enName: null,
  });

  const fetchBizConfig = useCallback((bizUuid: string) => {
    const url = alo7Api.prefix.host + `/api/v1/business_config/${bizUuid}`;
    alo7Api
      .fetch(url)
      .then((res: any) => setBizConfig(res))
      .catch(err => message.error(err.message));
  }, []);

  const updateBizConfig = (bizUuid: string, bizParams: any, callback: any) => {
    const url = alo7Api.prefix.host + `/api/v1/business_config/${bizUuid}`;
    alo7Api
      .fetch(url, {
        method: 'PUT',
        body: JSON.stringify(bizParams),
      })
      .then(() => {
        message.success('更新成功');
        if (callback) callback();
      })
      .catch(err => message.error(err.message));
  };

  const createBiz = (bizParams: any, callback: any) => {
    const url = alo7Api.prefix.host + `/api/v1/business_config`;
    alo7Api
      .fetch(url, {
        method: 'POST',
        body: JSON.stringify(bizParams),
      })
      .then(() => {
        message.success('更新成功');
        if (callback) callback();
      })
      .catch(err => message.error(err.message));
  };

  const deleteBiz = (bizUuid: string, callback: any) => {
    const url = alo7Api.prefix.host + `/api/v1/business_config/${bizUuid}`;
    alo7Api
      .fetch(url, {
        method: 'DELETE',
      })
      .then(() => {
        message.success('删除成功');
        if (callback) callback();
      })
      .catch(err => message.error(err.message));
  };

  return { bizConfig, fetchBizConfig, updateBizConfig, createBiz, deleteBiz };
}

export default useBizConfig;
